import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { RefObject, useEffect, useRef, useState } from 'react';
import IconLoader from '../../icons/loader';
import {
  BaseellaStatus,
  BaseellaStatusTransition,
  EnumNostroTabs,
  NostroTransaction,
  Signature,
  TransactionStatusLog,
  GeneralLedgerTransaction,
  Currency,
  Balance,
  DocumentComment,
  CASP,
  IntegrationLog,
} from '../types';
import TransactionStatusChange from '../incoming-outgoing-transaction-status-change';
import { SelectItemProps } from '../../select/types';
import Tabs from '../../tabs/tabs';
import NostroGeneralContent from './nostro-transaction-general';
import TransactionLogsContent from '../transaction-logs-content';
import ChangeLogs from '../../change-logs/change-logs';
import TransactionSignatures from '../signatures/signatures';
import TransactionAccountingEntries from '../accounting-entries/accounting-entries';
import TransactionComments from '../comments/comments';
import IconAttention from '../../icons/attention';
import ButtonPrimary from '../../button/button-primary';
import ButtonSecondary from '../../button/button-secondary';
import { ChangeLog, Pagination, PermissionEnum } from '../../../base-types';
import formatDate from '../../../helpers/format-date';
import IntegrationLogs from '../integration-logs/list';
import PermissionDenied from '../../permission-denied/permission-denied';
import CreationDisabledInfo from '../disable-creation-option/creation-disabled-info';

import styles from '../transaction.module.css';

interface NostroTransactionContentFormProps {
  transactionInfo?: NostroTransaction;
  isLoading?: boolean;
  statusesInfo?: {
    prevStatus?: BaseellaStatusTransition[];
    nextStatus?: BaseellaStatusTransition[];
  };
  onRollbackStatus?: (status: string) => void;
  onNextStatus?: (status: string) => void;
  control?: Control;
  errors?: FieldErrors<NostroTransaction>;
  isEditable?: boolean;
  currencies?: SelectItemProps[];
  paymentsTypes?: SelectItemProps[];
  toAccounts?: SelectItemProps[];
  fromAccounts?: SelectItemProps[];
  toPartnerPSPs?: SelectItemProps[];
  fromPartnerPSPs?: SelectItemProps[];
  toAccountsIsLoading?: boolean;
  fromAccountsIsLoading?: boolean;
  toPartnerPSPsIsLoading?: boolean;
  fromPartnerPSPsIsLoading?: boolean;
  onSearchAutocomplete?: (name: string, search: string) => void;
  paymentTypeIsFilled: boolean;
  toPartnerPspIsFilled: boolean;
  fromPartnerPspIsFilled: boolean;
  baseellaStatus?: BaseellaStatus;
  logs?: TransactionStatusLog[];
  isCreateForm?: boolean;
  changeLogs?: ChangeLog[];
  currencyId?: string;
  updateFromAccountBalance?: () => void;
  fromAccountBalance?: number;
  balanceFromAccountIsLoading?: boolean;
  toAccountBalance?: number;
  updateToAccountBalance?: () => void;
  balanceToAccountIsLoading?: boolean;
  signatures?: Signature[];
  setRejectPopupVisible?: (data: boolean) => void;
  sendCodeInProgress?: boolean;
  sendCode?: () => void;
  parentRef?: RefObject<HTMLDivElement | null>;
  parentScroll?: number;
  updateStatusIsLoading?: boolean;
  isLoadingStatuses?: boolean;
  activePermission?: PermissionEnum[];
  amount?: string;
  setAmount?: (data?: string) => void;
  latestQueuedStatus?: BaseellaStatus;
  createdByOrganizationId?: string;
  tabs: { name: string; value: EnumNostroTabs }[];
  activeTab: EnumNostroTabs;
  setActiveTab: (data: EnumNostroTabs) => void;
  setTransactionSidepanelVisible: (datA: boolean) => void;
  glTransactionsIsLoading?: boolean;
  glTransactions?: GeneralLedgerTransaction[];
  setGlTransactionSidePanelVisible: (data: boolean) => void;
  onGlTransactionClick?: (data: string) => void;
  canSignTransaction?: boolean;
  currenciesList?: Currency[];
  fields?: any;
  getGeneralLedgerTransactionValues?: () => void;
  isLoadingLogs?: boolean;
  getLogsTransactionByIdAction?: () => void;
  getSignaturesTransactionById?: () => void;
  isLoadingSignatures?: boolean;
  overdraftEnabled?: boolean;
  fromPspAccountsList?: any[];
  toPspAccountsList?: any[];
  fromAccountBalanceInfo?: Balance;
  toAccountBalanceInfo?: Balance;
  changeLogsIsLoading?: boolean;
  getTransactionChangeLogs?: (cursor?: string) => void;
  users?: SelectItemProps[];
  transactionChangeLogsPagination?: Pagination;
  changeLogsIsFetching?: boolean;
  transactionId?: string;
  transactionComments?: DocumentComment[];
  transactionCommentsPagination?: Pagination;
  getTransactionCommentsAction?: (cursor?: string) => void;
  commentsIsLoading?: boolean;
  commentsIsFetching?: boolean;
  onAddCommentToTransaction?: (data: string) => void;
  addCommentInProgress?: boolean;
  addCommentIsSuccess?: boolean;
  onEditCommentInTransaction?: (data: {
    text: string;
    commentId: string;
  }) => void;
  editTransactionCommentInProgress?: boolean;
  editTrancastionCommentIsSuccess?: boolean;
  onDeleteCommentInTransaction?: (commentId: string) => void;
  deleteCommentInTransactionInProgress?: boolean;
  deleteCommentInTranscationIsSuccess?: boolean;
  caspList?: CASP[];
  paymentType?: string;
  getIntegrationLogs?: any;
  integrationLogsIsLoading?: boolean;
  integrationLogsIsFetching?: boolean;
  integrationLogs?: IntegrationLog[];
  integrationLogsPagination?: Pagination;
  fromPspAccountsPagination?: Pagination;
  fromPspAccountsNextPageIsLoading?: boolean;
  fromPspAccountsOnScroll?: () => void;
  toPspAccountsPagination?: Pagination;
  toPspAccountsNextPageIsLoading?: boolean;
  toPspAccountsOnScroll?: () => void;
  setValue?: UseFormSetValue<any>;
  purposeCodeVisible?: boolean;
  purposeCodes?: SelectItemProps[];
  purposeCodesIsLoading?: boolean;
  purposeCodesNextPageIsLoading?: boolean;
  onScrollPurposeCodes?: () => void;
  purposeCodesPagination?: Pagination;
  clearErrors?: UseFormClearErrors<NostroTransaction>;
}

export default function NostroTransactionContentForm({
  transactionInfo,
  isLoading,
  statusesInfo,
  onNextStatus,
  onRollbackStatus,
  control,
  currencies,
  paymentsTypes,
  errors,
  isEditable,
  onSearchAutocomplete,
  toAccounts,
  fromAccounts,
  toPartnerPSPs,
  fromPartnerPSPs,
  toAccountsIsLoading,
  fromAccountsIsLoading,
  toPartnerPSPsIsLoading,
  fromPartnerPSPsIsLoading,
  paymentTypeIsFilled,
  toPartnerPspIsFilled,
  fromPartnerPspIsFilled,
  baseellaStatus,
  logs,
  isCreateForm,
  changeLogs,
  currencyId,
  updateFromAccountBalance,
  fromAccountBalance,
  balanceFromAccountIsLoading,
  toAccountBalance,
  updateToAccountBalance,
  balanceToAccountIsLoading,
  signatures,
  setRejectPopupVisible,
  sendCodeInProgress,
  sendCode,
  parentRef,
  parentScroll = 0,
  updateStatusIsLoading,
  isLoadingStatuses,
  activePermission,
  amount,
  setAmount,
  latestQueuedStatus,
  createdByOrganizationId,
  tabs,
  activeTab,
  setActiveTab,
  setTransactionSidepanelVisible,
  glTransactionsIsLoading,
  glTransactions,
  setGlTransactionSidePanelVisible,
  onGlTransactionClick,
  canSignTransaction,
  currenciesList,
  fields,
  getGeneralLedgerTransactionValues,
  isLoadingLogs,
  getLogsTransactionByIdAction,
  getSignaturesTransactionById,
  isLoadingSignatures,
  overdraftEnabled,
  fromPspAccountsList,
  toPspAccountsList,
  fromAccountBalanceInfo,
  toAccountBalanceInfo,
  getTransactionChangeLogs,
  changeLogsIsLoading,
  users,
  transactionChangeLogsPagination,
  changeLogsIsFetching,
  transactionId,
  transactionComments,
  transactionCommentsPagination,
  getTransactionCommentsAction,
  commentsIsLoading,
  commentsIsFetching,
  onAddCommentToTransaction,
  addCommentInProgress,
  addCommentIsSuccess,
  onEditCommentInTransaction,
  editTransactionCommentInProgress,
  editTrancastionCommentIsSuccess,
  onDeleteCommentInTransaction,
  deleteCommentInTransactionInProgress,
  deleteCommentInTranscationIsSuccess,
  caspList,
  paymentType,
  getIntegrationLogs,
  integrationLogsIsLoading,
  integrationLogsIsFetching,
  integrationLogs,
  integrationLogsPagination,
  fromPspAccountsPagination,
  fromPspAccountsNextPageIsLoading,
  fromPspAccountsOnScroll,
  toPspAccountsPagination,
  toPspAccountsNextPageIsLoading,
  toPspAccountsOnScroll,
  setValue,
  purposeCodeVisible,
  purposeCodes,
  purposeCodesIsLoading,
  purposeCodesNextPageIsLoading,
  onScrollPurposeCodes,
  purposeCodesPagination,
  clearErrors,
}: NostroTransactionContentFormProps): React.JSX.Element {
  const [signatureValid, setSignatureValid] = useState(true);

  let content;

  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      activeTab === EnumNostroTabs.GL_TRANSACTIONS &&
      getGeneralLedgerTransactionValues
    ) {
      getGeneralLedgerTransactionValues();
    }

    if (
      activeTab === EnumNostroTabs.LOG &&
      getLogsTransactionByIdAction &&
      activePermission?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_NOSTRO_LIST,
      )
    ) {
      getLogsTransactionByIdAction();
    }

    if (
      activeTab === EnumNostroTabs.SIGNATURES &&
      getSignaturesTransactionById
    ) {
      getSignaturesTransactionById();
    }

    if (
      activeTab === EnumNostroTabs.CHANGE_LOG &&
      getTransactionChangeLogs &&
      activePermission?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getTransactionChangeLogs();
    }

    if (
      activeTab === EnumNostroTabs.COMMENTS &&
      getTransactionCommentsAction &&
      activePermission?.includes(
        PermissionEnum.TRANSACTIONS_NOSTRO_COMMENTS_LIST,
      )
    ) {
      getTransactionCommentsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      transactionId &&
      baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS &&
      getSignaturesTransactionById
    ) {
      getSignaturesTransactionById();
    }
  }, [baseellaStatus, transactionId]);

  useEffect(() => {
    if (
      transactionId &&
      signatures &&
      signatures?.length > 0 &&
      baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS
    ) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const lastSignature = signatures[signatures?.length - 1];

      if (lastSignature) {
        setSignatureValid(lastSignature.valid as boolean);
      }
    }
  }, [signatures, baseellaStatus, transactionId]);

  if (isLoading) {
    content = (
      <div className={styles.loader}>
        <IconLoader className={styles.loaderIcon} />
      </div>
    );
  } else {
    content = (
      <>
        {canSignTransaction &&
          (baseellaStatus === BaseellaStatus.NOT_SIGNED ||
            (baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS &&
              !signatureValid)) && (
            <div className={styles.notSignedAlert}>
              <div className={styles.notSignedText}>
                <IconAttention width={24} height={24} />
                Additional signatures required
              </div>
              <div className={styles.notSignedControls}>
                {(!createdByOrganizationId ||
                  (baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS &&
                    !signatureValid)) && (
                  <ButtonPrimary
                    text={sendCodeInProgress ? 'Loading...' : 'Sign'}
                    onClick={sendCode}
                    className={styles.signButton}
                    disabled={sendCodeInProgress}
                    tabIndex={-1}
                  />
                )}
                {baseellaStatus === BaseellaStatus.NOT_SIGNED &&
                  !createdByOrganizationId && (
                    <ButtonSecondary
                      text="Reject"
                      onClick={() =>
                        setRejectPopupVisible && setRejectPopupVisible(true)
                      }
                      tabIndex={-1}
                    />
                  )}
              </div>
            </div>
          )}
        {statusesInfo && !transactionInfo?.deletedAt && (
          <TransactionStatusChange
            transaction={statusesInfo}
            baseellaStatus={baseellaStatus}
            onNextStatus={onNextStatus}
            onRollbackStatus={onRollbackStatus}
            updateStatusIsLoading={updateStatusIsLoading}
            isLoadingStatuses={isLoadingStatuses}
            latestQueuedStatus={latestQueuedStatus}
            activeTab={activeTab}
          />
        )}
        {transactionInfo?.deletedAt && (
          <div className={styles.deleted}>{`Transaction deleted ${formatDate(
            transactionInfo?.deletedAt,
            true,
          )}`}</div>
        )}
        {!isCreateForm && (
          <Tabs
            tabs={tabs}
            onClick={(value) => {
              setActiveTab(value as EnumNostroTabs);

              if (parentScroll >= Number(scrollRef.current?.offsetTop) - 130) {
                parentRef?.current?.scrollTo({
                  top: Number(scrollRef.current?.offsetTop) - 130,
                });
              }
            }}
            activeTab={activeTab}
            itemClassName={styles.transactionTab}
            moreButtonClassName={styles.transactionMoreButton}
            isTransactionsTabs
            withShadow={
              // eslint-disable-next-line react-hooks/refs
              parentScroll > Number(scrollRef.current?.offsetTop) - 130
            }
            withSticky
          />
        )}
        {transactionId && fields?.disableExternalTransactionCreate && (
          <CreationDisabledInfo />
        )}

        <div
          ref={scrollRef}
          className={`${
            activeTab === EnumNostroTabs.GL_TRANSACTIONS
              ? styles.sidepanelContentWrapper
              : ''
          } ${styles.siddepanelContentFlex}`}
        >
          <div
            className={
              activeTab === EnumNostroTabs.GENERAL
                ? styles.activeTabContent
                : styles.tabContent
            }
          >
            <NostroGeneralContent
              transactionInfo={transactionInfo}
              isLoading={isLoading}
              control={control}
              errors={errors}
              isEditable={isEditable}
              currencies={currencies}
              paymentsTypes={paymentsTypes}
              toAccounts={toAccounts}
              fromAccounts={fromAccounts}
              toPartnerPSPs={toPartnerPSPs}
              fromPartnerPSPs={fromPartnerPSPs}
              toAccountsIsLoading={toAccountsIsLoading}
              fromAccountsIsLoading={fromAccountsIsLoading}
              toPartnerPSPsIsLoading={toPartnerPSPsIsLoading}
              fromPartnerPSPsIsLoading={fromPartnerPSPsIsLoading}
              onSearchAutocomplete={onSearchAutocomplete}
              paymentTypeIsFilled={paymentTypeIsFilled}
              toPartnerPspIsFilled={toPartnerPspIsFilled}
              fromPartnerPspIsFilled={fromPartnerPspIsFilled}
              isCreateForm={isCreateForm}
              currencyId={currencyId}
              updateFromAccountBalance={updateFromAccountBalance}
              fromAccountBalance={fromAccountBalance}
              balanceFromAccountIsLoading={balanceFromAccountIsLoading}
              toAccountBalance={toAccountBalance}
              updateToAccountBalance={updateToAccountBalance}
              balanceToAccountIsLoading={balanceToAccountIsLoading}
              activePermission={activePermission}
              amount={amount}
              setAmount={setAmount}
              currenciesList={currenciesList}
              fields={fields}
              overdraftEnabled={overdraftEnabled}
              fromPspAccountsList={fromPspAccountsList}
              toPspAccountsList={toPspAccountsList}
              fromAccountBalanceInfo={fromAccountBalanceInfo}
              toAccountBalanceInfo={toAccountBalanceInfo}
              baseellaStatus={baseellaStatus}
              caspList={caspList}
              paymentType={paymentType}
              fromPspAccountsPagination={fromPspAccountsPagination}
              fromPspAccountsNextPageIsLoading={
                fromPspAccountsNextPageIsLoading
              }
              fromPspAccountsOnScroll={fromPspAccountsOnScroll}
              toPspAccountsPagination={toPspAccountsPagination}
              toPspAccountsNextPageIsLoading={toPspAccountsNextPageIsLoading}
              toPspAccountsOnScroll={toPspAccountsOnScroll}
              transactionId={transactionId}
              setValue={setValue}
              purposeCodeVisible={purposeCodeVisible}
              purposeCodes={purposeCodes}
              purposeCodesIsLoading={purposeCodesIsLoading}
              purposeCodesNextPageIsLoading={purposeCodesNextPageIsLoading}
              onScrollPurposeCodes={onScrollPurposeCodes}
              purposeCodesPagination={purposeCodesPagination}
              clearErrors={clearErrors}
            />
          </div>

          {!isCreateForm && (
            <div
              className={
                activeTab === EnumNostroTabs.LOG
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionLogsContent
                isLoading={isLoadingLogs as boolean}
                logs={logs}
                activePermission={activePermission?.includes(
                  PermissionEnum.TRANSACTION_QUEUE_ITEMS_NOSTRO_LIST,
                )}
              />
            </div>
          )}
          {!isCreateForm && (
            <div
              className={
                activeTab === EnumNostroTabs.CHANGE_LOG
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <ChangeLogs
                entityType="TRANSACTION"
                entityId={transactionId as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermission}
                users={users}
                pagination={transactionChangeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getTransactionChangeLogs}
              />
            </div>
          )}
          {!isCreateForm && (
            <div
              className={
                activeTab === EnumNostroTabs.SIGNATURES
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionSignatures
                signatures={signatures}
                isLoadingSignatures={isLoadingSignatures}
              />
            </div>
          )}
          {!isCreateForm && (
            <div
              className={
                activeTab === EnumNostroTabs.GL_TRANSACTIONS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionAccountingEntries
                glTransactionsIsLoading={glTransactionsIsLoading}
                glTransactions={glTransactions}
                setGlTransactionSidePanelVisible={
                  setGlTransactionSidePanelVisible
                }
                onGlTransactionClick={onGlTransactionClick}
                setTransactionSidepanelVisible={setTransactionSidepanelVisible}
                currenciesList={currenciesList}
              />
            </div>
          )}
          {!isCreateForm && (
            <div
              className={
                activeTab === EnumNostroTabs.COMMENTS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionComments
                comments={transactionComments}
                pagination={transactionCommentsPagination}
                isLoading={commentsIsLoading}
                isFetching={commentsIsFetching}
                commentsAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_NOSTRO_COMMENTS_LIST,
                )}
                getCommentsList={getTransactionCommentsAction}
                createAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_NOSTRO_COMMENTS_CREATE_ACTION,
                )}
                editAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_NOSTRO_COMMENTS_EDIT_ACTION,
                )}
                deleteAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_NOSTRO_COMMENTS_DELETE_ACTION,
                )}
                onAddCommentToTransaction={onAddCommentToTransaction}
                addCommentInProgress={addCommentInProgress}
                addCommentIsSuccess={addCommentIsSuccess}
                onEditCommentInTransaction={onEditCommentInTransaction}
                editTransactionCommentInProgress={
                  editTransactionCommentInProgress
                }
                editTrancastionCommentIsSuccess={
                  editTrancastionCommentIsSuccess
                }
                onDeleteCommentInTransaction={onDeleteCommentInTransaction}
                deleteCommentInTransactionInProgress={
                  deleteCommentInTransactionInProgress
                }
                deleteCommentInTranscationIsSuccess={
                  deleteCommentInTranscationIsSuccess
                }
                changeLogsAvailable={activePermission?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                )}
                users={users}
              />
            </div>
          )}
          {!isCreateForm && (
            <div
              className={
                activeTab === EnumNostroTabs.INTEGRATION_LOGS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              {getIntegrationLogs &&
              activePermission?.includes(
                PermissionEnum.PSP_INTEGRATION_LOGS_LIST,
              ) ? (
                <IntegrationLogs
                  getIntegrationLogs={getIntegrationLogs}
                  integrationLogs={integrationLogs}
                  integrationLogsIsFetching={integrationLogsIsFetching}
                  integrationLogsIsLoading={integrationLogsIsLoading}
                  integrationLogsPagination={integrationLogsPagination}
                  activePermissions={activePermission}
                  activeTab={activeTab}
                  pspPartnerId={fields?.fromPartnerPspId}
                  transactionId={transactionId}
                />
              ) : (
                <>
                  <div className={styles.changeLogsTitle}>Integration log</div>
                  <PermissionDenied />
                </>
              )}
            </div>
          )}
        </div>
      </>
    );
  }

  return content;
}
